import {Center, createStyles, Loader, Text} from '@mantine/core';
import React, {useCallback, useEffect, useMemo, useReducer, useRef, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {SelectedParentStorable} from 'constants/SampleConstants';
import {DataFrame} from 'data-frames/DataFrameTypes';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {MiniPlateSummaryFilledPosition} from 'notebook/integrations/plate-maps/scratch-plates/MiniPlateSummary';
import {
  METADATA_LAYER_NAME,
  parsePlateFieldInfo,
  parsePlateFieldValues,
} from 'notebook/integrations/plate-maps/scratch-plates/ScratchPlateDesignSummaryUtil';
import notifications from 'notifications';
import {
  BulkPutScratchPlateInInventoryItem,
  listScratchPlateMappableColumns,
  MappableLayer,
  ScratchPlateColumnMapping,
  ScratchPlatePlateFieldMapping,
} from 'plates/dao/ScratchPlateDAO';
import BaseStorablePaneStudyableFormGroup from 'samples/BaseStorablePaneStudyableFormGroup';
import ParentStorableDataSelector from 'samples/ParentStorableDataSelector';
import ScratchPlateBarcodeInput, {
  BarcodeMode,
} from 'samples/plate-maps/scratch-plates/ScratchPlateBarcodeInput';
import {
  getUnionPlateFields,
  isPlateSchemaFieldCompatible,
} from 'samples/plate-maps/scratch-plates/ScratchPlateDesignModalUtil';
import {submitBulkPutInInventory} from 'samples/plate-maps/scratch-plates/ScratchPlateInventoryActions';
import SelectPlatesStep from 'samples/plate-maps/scratch-plates/SelectPlatesStep';
import PlateSchemaSelector from 'samples/PlateSchemaSelector';
import * as SampleSchemaActions from 'samples/SampleSchemaActions';
import SampleSchemaStore from 'samples/SampleSchemaStore';
import {getEmptySelectedParent} from 'samples/storables/ParentStorableUtil';
import {PlateSchema, PlateSchemaType, PlateSize} from 'samples/storables/StorableSchemaConstants';
import {ConnectedStorableSourceSelector} from 'samples/storables/StorableSourceSelector';
import getTestProps from 'test/lib/cypress/getTestProps';
import FormGroup from 'ui-library/bs/FormGroup';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import legacyRunModal from 'ui-library/legacyRunModal';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import StepNav, {StepItemStatus, StepNavSize, StepNavVariant} from 'ui-library/StepNav';

import {
  GetScratchPlatesPlateFields,
  GetScratchPlatesPlateFieldsVariables,
} from './__generated/GetScratchPlatesPlateFields';
import {GET_SCRATCH_PLATES_PLATE_FIELDS_QUERY} from './GetScratchPlatesPlateFields';
import ScratchPlateColumnMappingTable from './ScratchPlateColumnMappingTable';
import ScratchPlatePlateFieldMappingTable from './ScratchPlatePlateFieldMappingTable';

export interface ScratchPlateInput {
  scratchPlateId: string;
  scratchPlateName: string | null;
  plateSize: PlateSize;
  plateSchemaId: string | null;
  /** Non-null when the plate has already been put in inventory; such plates are excluded from selection. */
  inventoryPlateId: string | null;
  /** Null when the plate has no summary yet, so the selection step renders no preview. */
  filledPositions: ReadonlyArray<MiniPlateSummaryFilledPosition> | null;
  /** ISO-8601 string; null when unknown, so the selection step renders no created-at time. */
  createdAt: string | null;
}

enum PutScratchPlateInInventoryModalStep {
  SELECT_PLATES = 'SELECT_PLATES',
  CONFIGURE = 'CONFIGURE',
}

const STEPS = [
  {key: PutScratchPlateInInventoryModalStep.SELECT_PLATES, title: 'Select plates'},
  {key: PutScratchPlateInInventoryModalStep.CONFIGURE, title: 'Configure details'},
];

const STATUS_BY_STEP: {[key in PutScratchPlateInInventoryModalStep]: StepItemStatus | null} = {
  [PutScratchPlateInInventoryModalStep.SELECT_PLATES]: null,
  [PutScratchPlateInInventoryModalStep.CONFIGURE]: null,
};

export interface PutScratchPlateInInventoryModalProps {
  plates: Array<ScratchPlateInput>;
  showSelectPlatesStep?: boolean;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
  onCompleted?: () => void;
}

function PutScratchPlateInInventoryModal({
  plates,
  onRequestClose,
  onRequestSubmit,
  onCompleted,
  showSelectPlatesStep = true,
}: PutScratchPlateInInventoryModalProps): JSX.Element {
  // The modal seeds its shared fields from plates[0]. openPutScratchPlateInInventoryModal
  // guards this too, but the component is exported and rendered directly (e.g. in tests),
  // so fail fast with a clear message rather than crashing on plates[0] being undefined.
  if (plates.length === 0) {
    throw new Error('PutScratchPlateInInventoryModal requires at least one plate.');
  }
  const {classes} = useStyles();
  // All potential plates come from one design and so share dimensions; use the
  // first plate to filter schemas and to seed the shared schema default.
  const plateSize = plates[0].plateSize;
  const initialPlateSchemaId = plates[0].plateSchemaId;

  // Plates already in inventory can't be put in inventory again, so they're
  // never offered as a choice on the selection step.
  const eligiblePlates = plates.filter((plate) => plate.inventoryPlateId == null);
  if (eligiblePlates.length === 0) {
    throw new Error('PutScratchPlateInInventoryModal requires at least one plate not already in inventory.');
  }

  const [currentStep, setCurrentStep] = useState(
    showSelectPlatesStep
      ? PutScratchPlateInInventoryModalStep.SELECT_PLATES
      : PutScratchPlateInInventoryModalStep.CONFIGURE
  );
  // When the selection step is skipped, every eligible plate is implicitly
  // selected since the user never gets a chance to choose a subset.
  const [selectedPlateIds, setSelectedPlateIds] = useState<ReadonlySet<string>>(
    () => new Set(showSelectPlatesStep ? [] : eligiblePlates.map((plate) => plate.scratchPlateId))
  );
  const selectedPlates = eligiblePlates.filter((plate) => selectedPlateIds.has(plate.scratchPlateId));
  const isSinglePlate = selectedPlates.length === 1;

  function handleToggleSelected(scratchPlateId: string): void {
    setSelectedPlateIds((prev) => {
      const next = new Set(prev);
      if (next.has(scratchPlateId)) {
        next.delete(scratchPlateId);
      } else {
        next.add(scratchPlateId);
      }
      return next;
    });
  }

  function handleSelectAll(): void {
    setSelectedPlateIds(new Set(eligiblePlates.map((plate) => plate.scratchPlateId)));
  }

  function handleDeselectAll(): void {
    setSelectedPlateIds(new Set());
  }

  const [isSubmitting, setIsSubmitting] = useState(false);
  // A successful submit calls onRequestSubmit, which unmounts this modal. The submit's
  // `finally` would then setState on an unmounted component, so guard against that.
  const isMountedRef = useRef(true);
  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    []
  );

  const [barcodeMode, setBarcodeMode] = useState<BarcodeMode>('autogenerate');
  const [customBarcode, setCustomBarcode] = useState('');
  const [selectedPlateSchemaId, setSelectedPlateSchemaId] = useState<string | null>(initialPlateSchemaId);

  const [selectedParent, setSelectedParent] = useState<SelectedParentStorable>(getEmptySelectedParent);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [studyIds, setStudyIds] = useState<Array<string>>([]);

  const [mappableLayers, setMappableLayers] = useState<Array<MappableLayer>>([]);
  const [columnMappings, setColumnMappings] = useState<Array<ScratchPlateColumnMapping>>([]);
  const [plateFieldMappings, setPlateFieldMappings] = useState<Array<ScratchPlatePlateFieldMapping>>([]);

  const [, forceUpdate] = useReducer((x: number) => x + 1, 0);
  useEffect(() => {
    SampleSchemaStore.addChangeListener(forceUpdate);
    // Container schemas hold the settable fields that columns map onto; ensure
    // they are loaded so selectedContainerSchema resolves once a schema is picked.
    SampleSchemaActions.fetchAllContainerSchemas();
    return () => {
      SampleSchemaStore.removeChangeListener(forceUpdate);
    };
  }, []);

  // The mappable source columns live on the scratch plate, independent of the
  // chosen plate schema. The shared mapping applies to every plate, so fetch
  // the representative set from the first eligible plate once.
  const representativePlateId = eligiblePlates[0].scratchPlateId;
  useEffect(() => {
    let cancelled = false;
    listScratchPlateMappableColumns(representativePlateId)
      .then((response) => {
        if (!cancelled) {
          setMappableLayers(response.layers);
        }
      })
      .catch(() => {
        // Column mapping is optional; failing to load source columns simply
        // hides the mapping table rather than blocking plate creation.
      });
    return () => {
      cancelled = true;
    };
  }, [representativePlateId]);

  const selectedPlateSchema: PlateSchema | null =
    selectedPlateSchemaId != null ? SampleSchemaStore.getPlateSchema(selectedPlateSchemaId) : null;

  const selectedContainerSchema =
    selectedPlateSchema?.containerSchemaId != null
      ? SampleSchemaStore.getContainerSchema(selectedPlateSchema.containerSchemaId)
      : null;

  // Plates with no schema of their own don't get their plate-level fields copied automatically (see
  // the backend's same-schema copy in bulk_put_scratch_plates_in_inventory), so offer a manual
  // mapping instead, scoped to just this subset — schema-linked plates in the same batch are
  // unaffected. Every ad hoc field name present on any plate in this subset is offered; a plate that
  // lacks a given name just gets a null value for the field it's mapped to (see
  // bulk_put_scratch_plates_in_inventory's plate-field-mapping apply step) rather than that name
  // being withheld from every plate's mapping options.
  const schemaLessPlates = selectedPlates.filter((plate) => plate.plateSchemaId == null);
  const schemaLessPlateIds = schemaLessPlates.map((plate) => plate.scratchPlateId);
  const plateFieldsQuery = useSkippableBenchlingQuery<
    GetScratchPlatesPlateFields,
    GetScratchPlatesPlateFieldsVariables
  >(GET_SCRATCH_PLATES_PLATE_FIELDS_QUERY, {
    variables: {ids: schemaLessPlateIds, limit: 1},
    skip: schemaLessPlateIds.length === 0,
  });

  const unionMetadataFields = useMemo(() => {
    if (plateFieldsQuery.type !== QueryResultType.SUCCESS) {
      return [];
    }
    const perPlateFields = plateFieldsQuery.data.scratchPlates.map((scratchPlate) => {
      if (scratchPlate.__typename !== 'ScratchPlateNode') {
        return [];
      }
      const metadataLayer = scratchPlate.layers.find((layer) => layer.name === METADATA_LAYER_NAME);
      const dataFrame = metadataLayer?.dataFrame;
      if (dataFrame == null || isInaccessible(dataFrame)) {
        return [];
      }
      const columns = dataFrame.allColumnsJson as unknown as DataFrame['columns'];
      const dataFrameJson = dataFrame.dataFrameJson as unknown as DataFrame;
      const fields = parsePlateFieldValues(dataFrameJson, parsePlateFieldInfo(columns));
      return fields.map((field) => ({name: field.name, type: field.type}));
    });
    return getUnionPlateFields(perPlateFields);
  }, [plateFieldsQuery]);

  const plateSchemaFilter = useCallback(
    (schema: PlateSchema): boolean => schema.height === plateSize[0] && schema.width === plateSize[1],
    [plateSize]
  );

  const handlePlateSchemaChange = useCallback((schema: PlateSchema | null) => {
    setSelectedPlateSchemaId(schema?.id ?? null);
    // Mappings target a specific schema's fields, so drop them when the schema
    // changes rather than carrying over stale field references.
    setColumnMappings([]);
    setPlateFieldMappings([]);
  }, []);

  const registryId = selectedPlateSchema?.registryId;

  const isSelectionValid = selectedPlates.length > 0;
  const isDetailsValid = selectedPlateSchemaId != null;
  const isProjectValid =
    !BenchlingConfig.ENABLE_PROJECTS_REQUIRED_FOR_NON_LOCATION_STORABLES ||
    (selectedProjectId != null && selectedProjectId !== registryId);
  const isLocationValid = selectedParent.storable != null;
  const canSubmit = isDetailsValid && isProjectValid && isLocationValid && !isSubmitting;

  async function handleConfigureSubmit(): Promise<void> {
    setIsSubmitting(true);
    try {
      const items: Array<BulkPutScratchPlateInInventoryItem> = selectedPlates.map((plate) => ({
        scratchPlateId: plate.scratchPlateId,
        // Custom barcode only applies to a single plate
        barcode:
          isSinglePlate && barcodeMode === 'custom' && customBarcode.trim()
            ? {barcodeMode: 'SPECIFY_BARCODE' as const, barcode: customBarcode.trim()}
            : barcodeMode === 'useNameAsBarcode'
              ? {barcodeMode: 'USE_NAME' as const, useNameAsBarcode: true}
              : undefined,
      }));

      // Resolves once the request is accepted, not when the job finishes: a
      // tray task tracks the rest, so we close the modal right away rather than
      // blocking on a potentially long job.
      await submitBulkPutInInventory(
        {
          defaults: {
            plateSchemaId: selectedPlateSchemaId ?? undefined,
            sourceId: selectedProjectId ?? undefined,
            parentStorableId: selectedParent.storable?.id ?? undefined,
            columnMappings: columnMappings.length > 0 ? columnMappings : undefined,
            plateFieldMappings: plateFieldMappings.length > 0 ? plateFieldMappings : undefined,
            studyIds: studyIds.length > 0 ? studyIds : undefined,
          },
          items,
        },
        {onCompleted}
      );

      onRequestSubmit();
    } catch (error: unknown) {
      if (error instanceof Error) {
        notifications.error(error.message);
      } else if (error != null && typeof error === 'object') {
        notifications.notifyError(error as Response);
      } else {
        throw error;
      }
    } finally {
      // Skip the state update if a successful submit already unmounted the modal.
      if (isMountedRef.current) {
        setIsSubmitting(false);
      }
    }
  }

  async function handleSubmit(): Promise<void> {
    if (currentStep === PutScratchPlateInInventoryModalStep.SELECT_PLATES) {
      setCurrentStep(PutScratchPlateInInventoryModalStep.CONFIGURE);
      return;
    }
    await handleConfigureSubmit();
  }

  let componentBody: JSX.Element;
  let buttons: Array<ModalButton>;
  if (currentStep === PutScratchPlateInInventoryModalStep.SELECT_PLATES) {
    buttons = [
      {text: 'Cancel', cancel: true, style: 'default'},
      {text: 'Next', submit: true, style: 'primary', disabled: !isSelectionValid},
    ];
    componentBody = (
      <SelectPlatesStep
        plates={eligiblePlates}
        plateSize={plateSize}
        selectedPlateIds={selectedPlateIds}
        onToggleSelected={handleToggleSelected}
        onSelectAll={handleSelectAll}
        onDeselectAll={handleDeselectAll}
      />
    );
  } else {
    buttons = [
      ...(showSelectPlatesStep
        ? [
            {
              text: 'Back',
              onClick: () => {
                setCurrentStep(PutScratchPlateInInventoryModalStep.SELECT_PLATES);
              },
            },
          ]
        : []),
      {text: isSubmitting ? 'Creating...' : 'Create', submit: true, style: 'primary', disabled: !canSubmit},
    ];
    componentBody = (
      <>
        <FormGroup>
          <FieldLabel isRequired={true} label="Plate schema " labelStyle={FieldLabel.STYLES.LABEL} />
          <Text className={classes.helpText}>
            Only schemas matching the plate dimensions ({plateSize[0]} x {plateSize[1]}) are shown.
          </Text>
          <PlateSchemaSelector
            plateSchemaType={PlateSchemaType.FIXED_PLATE}
            plateSchemaFilter={plateSchemaFilter}
            onChange={handlePlateSchemaChange}
            registryId={undefined}
            selectedPlateSchemaId={selectedPlateSchemaId}
            disabled={false}
            shouldShowNoSchemaOption={false}
          />
        </FormGroup>

        {schemaLessPlates.length > 0 && selectedPlateSchema != null && (
          <FormGroup>
            <FieldLabel label="Plate field mapping" labelStyle={FieldLabel.STYLES.LABEL} />
            <Text className={classes.helpText}>
              Some of the selected plates have no schema of their own, so their plate-level metadata fields
              have no automatic correspondence to the new plate schema&apos;s fields. Map them below to carry
              values over. Schema fields that are not shown here can only be configured after the inventory
              plate has been created.
            </Text>
            {plateFieldsQuery.type === QueryResultType.PENDING ? (
              <Center
                {...getTestProps({
                  component: 'PutScratchPlateInInventoryModal',
                  element: 'plateFieldMappingLoading',
                })}
              >
                <Loader />
              </Center>
            ) : (
              <ScratchPlatePlateFieldMappingTable
                schemaFields={selectedPlateSchema.fields.filter((field) =>
                  isPlateSchemaFieldCompatible(field)
                )}
                metadataFields={unionMetadataFields}
                mappings={plateFieldMappings}
                setMappings={setPlateFieldMappings}
              />
            )}
          </FormGroup>
        )}

        {selectedContainerSchema != null && mappableLayers.length > 0 && (
          <FormGroup>
            <FieldLabel label="Column mapping" labelStyle={FieldLabel.STYLES.LABEL} />
            <Text className={classes.helpText}>
              Map columns from the plate onto fields on the new {isSinglePlate ? 'plate' : 'plates'}&apos;
              wells.
            </Text>
            <ScratchPlateColumnMappingTable
              schemaFields={selectedContainerSchema.fields}
              layers={mappableLayers}
              mappings={columnMappings}
              setMappings={setColumnMappings}
            />
          </FormGroup>
        )}

        <FormGroup>
          <ParentStorableDataSelector
            label="Location "
            isRequired={true}
            selectedParent={selectedParent}
            onUpdate={setSelectedParent}
            registryId={registryId}
            storableSchema={
              selectedPlateSchema != null
                ? {id: selectedPlateSchema.id, registryId: selectedPlateSchema.registryId}
                : null
            }
            allowNone={false}
          />
        </FormGroup>

        <ScratchPlateBarcodeInput
          isSinglePlate={isSinglePlate}
          mode={barcodeMode}
          customBarcode={customBarcode}
          plateName={selectedPlates[0]?.scratchPlateName ?? null}
          onModeChange={setBarcodeMode}
          onCustomBarcodeChange={setCustomBarcode}
        />

        <FormGroup>
          <FieldLabel label="Project" labelStyle={FieldLabel.STYLES.LABEL} />
          <ConnectedStorableSourceSelector
            isWritable={true}
            onChange={setSelectedProjectId}
            registryId={registryId ?? null}
            selectedSourceId={selectedProjectId}
            selectorModalZIndex={1060}
          />
        </FormGroup>

        {BenchlingConfig.STUDIES_ENABLE && (
          <BaseStorablePaneStudyableFormGroup
            studyIds={studyIds}
            onUpdate={(state) => {
              setStudyIds(state.studyIds ?? []);
            }}
          />
        )}
      </>
    );
  }

  return (
    <Modal
      title={`Put plate${eligiblePlates.length > 1 ? 's' : ''} in inventory`}
      large={true}
      onRequestClose={onRequestClose}
      onRequestSubmit={handleSubmit}
      buttons={buttons}
    >
      {showSelectPlatesStep && (
        <>
          <StepNav<PutScratchPlateInInventoryModalStep>
            currentStepKey={currentStep}
            onUpdateCurrentStep={(step) => {
              // Only allow navigating back to plate selection; advancing to the
              // configure step must go through the validated "Next" button.
              if (step === PutScratchPlateInInventoryModalStep.SELECT_PLATES) {
                setCurrentStep(step);
              }
            }}
            steps={STEPS}
            statusByStep={STATUS_BY_STEP}
            size={StepNavSize.SMALL}
            variant={StepNavVariant.FLAT}
          />
          <hr className={classes.stepDivider} />
        </>
      )}
      {componentBody}
    </Modal>
  );
}

export default PutScratchPlateInInventoryModal;

const useStyles = createStyles((theme) => ({
  helpText: {
    color: theme.colors.gray[6],
    fontSize: theme.fontSizes.sm,
    marginBottom: theme.spacing.xs,
  },
  stepDivider: {
    marginLeft: '-16px',
    marginRight: '-16px',
  },
}));

export function openPutScratchPlateInInventoryModal(
  props: Pick<PutScratchPlateInInventoryModalProps, 'plates' | 'showSelectPlatesStep' | 'onCompleted'> & {
    /** Called after the request is accepted, e.g. to clear the caller's selection. */
    onSubmitted?: () => void;
  }
): void {
  const {plates, showSelectPlatesStep, onSubmitted, onCompleted} = props;
  // The modal seeds its shared fields from plates[0], so an empty list is a
  // caller bug (e.g. opening the modal with nothing selected). Bail out with a
  // clear error rather than mounting a modal that would crash on plates[0].
  if (plates.length === 0) {
    throw new Error('openPutScratchPlateInInventoryModal requires at least one plate.');
  }

  legacyRunModal(PutScratchPlateInInventoryModal, {
    plates,
    showSelectPlatesStep,
    onCompleted,
    onRequestSubmit: () => {
      onSubmitted?.();
    },
  });
}
